import { OverrideBehaviour, PollingMode, User, deserializeConfig, getClient, } from '@configcat/sdk';
const DEVICE_ID_STORAGE_KEY = 'configcat-device-id';
const PLACEHOLDER_EMAIL = 'placeholder';
// not all browsers support localStorage, so we use a local in-memory fallback
let cachedDeviceId;
export function getFlags(rules, user, options) {
    // Intentionally return an empty flags object if rules are empty
    if (!rules)
        return { success: true, flags: Object.freeze({}) };
    try {
        // The SDK key argument is the rules string on purpose. For LocalOnly mode, `rules` is a good unique identifier.
        const client = getClient(rules, PollingMode.ManualPoll, {
            logger: options?.logger,
            setupHooks: options?.onFlagEvaluated ? (hooks) => hooks.on('flagEvaluated', options.onFlagEvaluated) : undefined,
            flagOverrides: {
                // LocalOnly evaluates this data source instead of the CDN. https://configcat.com/docs/sdk-reference/js/overview/#custom-data-source-implementation
                dataSource: new EmbeddedConfigJsonDataSource(rules),
                behaviour: OverrideBehaviour.LocalOnly,
            },
        });
        const snapshot = client.snapshot();
        const flagMap = {};
        for (const key of snapshot.getAllKeys()) {
            flagMap[key] = snapshot.getValue(key, undefined, toSdkUser(user));
        }
        return { success: true, flags: Object.freeze(flagMap) };
    }
    catch (error) {
        return { success: false, error };
    }
}
function toSdkUser(user) {
    const resolved = user ?? {};
    const id = resolved.id ?? '';
    const deviceId = getOrCreateDeviceId();
    return new User(id, resolved.email ?? PLACEHOLDER_EMAIL, '', {
        tenant: resolved.tenant ?? '',
        roles: resolved.roles ?? '',
        tags: resolved.tags ?? [],
        random_device_id: deviceId,
        rollout_id: id || deviceId,
    });
}
function getOrCreateDeviceId() {
    try {
        const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
        if (existing) {
            cachedDeviceId = existing;
            return existing;
        }
    }
    catch {
        // Private mode / missing localStorage — fall through to the module singleton.
    }
    if (!cachedDeviceId) {
        cachedDeviceId = generateDeviceId();
    }
    try {
        localStorage.setItem(DEVICE_ID_STORAGE_KEY, cachedDeviceId);
    }
    catch {
        // Ignore persist failure (quota / Android webview). The module let keeps this id for the session.
    }
    return cachedDeviceId;
}
function generateDeviceId() {
    if (typeof crypto?.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    /* v8 ignore next - crypto.randomUUID missing in some webviews -- @preserve */
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}
/**
 * Serves the already-fetched config_v6 JSON as LocalOnly overrides.
 * Same class as ConfigCat's custom data source sample: https://configcat.com/docs/sdk-reference/js/overview/#custom-data-source-implementation
 */
class EmbeddedConfigJsonDataSource {
    settings;
    constructor(configJson) {
        this.settings = deserializeConfig(configJson).f ?? {};
    }
    getOverrides() {
        return this.settings;
    }
}
